/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { msalInstance, loginRequest } from '../authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { ApiResponse, ApiError } from '@shared/types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export async function getAccessToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No active account');
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const response = await msalInstance.acquireTokenPopup(loginRequest);
      return response.accessToken;
    }
    throw err;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  skipAuth = false
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (!skipAuth) {
    try {
      const token = await getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch {
      // If no token available, proceed without — backend will return 401
    }
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const errorBody: ApiError = await res.json().catch(() => ({
      error: 'UNKNOWN',
      message: res.statusText,
    }));
    throw Object.assign(new Error(errorBody.message), {
      statusCode: res.status,
      ...errorBody,
    });
  }

  return res.json();
}

export const apiClient = {
  get: <T>(path: string, skipAuth?: boolean) => request<T>('GET', path, undefined, skipAuth),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
